import { ActionType, PlayerAction } from '../types/actions';
import {
  CardInstance,
  CardPosition,
  MatchState,
  Seat,
  isOpeningTurn,
  otherSeat,
} from '../types/state';
import {
  CardLibrary,
  getCardData,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
} from './helpers';
import { attackLockCode, cardAttacksDirectly, DARK_ELF, DARK_ELF_COST, getEquipConfig, getFieldSpellConfig, JIRAI_GUMO, magnetismTargets, THE_BISTRO_BUTCHER } from './effects';
import { aiIsHighDifficulty, plannerDifficulty } from './aiDeck';
import { EXODIA_PIECE_SET } from './aiRecipes';

const BATTLE_BACKROW = new Set([
  '12607053',
  '83887306',
  '24068492',
  '17814387',
  '44209392',
  '77622396',
  '44095762', // Mirror Force
  '88279736', // Robbin' Goblin
]);

/** Id this seat has seen. Face-down cards outside this list stay unknown. */
export function knownCardId(state: MatchState, seat: Seat, uid: string): string | null {
  const list = state.aiKnownCards?.[seat] ?? [];
  return list.find((known) => known.uid === uid)?.cardId ?? null;
}

/** A Set that can still answer an attack. Known non-battle cards and old passes do not. */
export function backrowStillScary(
  state: MatchState,
  seat: Seat,
  card: CardInstance,
): boolean {
  if (card.faceUp || card.setOnTurn >= state.absoluteTurnCount) return false;
  if ((state.battlePassedUids ?? []).includes(card.uid)) return false;
  const known = knownCardId(state, seat, card.uid);
  if (known && !BATTLE_BACKROW.has(known)) return false;
  return true;
}

const ADVANCE: PlayerAction = { type: ActionType.ADVANCE_PHASE };
/** Below this, a body stays off an unknown Set. A 1200 still runs in. */
export const SET_ATTACK_MIN_ATK = 1000;

export interface CombatForecast {
  /** LP the attacker would deal this battle after the matching. */
  attackerDamage: number;
  /** ATK of defender face-up monsters this matching would destroy. */
  defenderAtkDestroyed: number;
  /** ATK of attacking monsters lost to trades (equal ATK). */
  attackerAtkLost: number;
  /** True when every defender is face-up and leftover damage >= their LP. */
  lethal: boolean;
  /** Face-up ATK still standing on the defender's field after the matching. */
  unclearedDefenderAtk: number;
  leftoverAttackers: number;
}

interface Barrier {
  monster: CardInstance;
  stat: number;
  isAtk: boolean;
  threat: number;
}

interface AttackPlan {
  attackerUid: string;
  targetUid: string | null;
}

function atkOf(m: CardInstance, library: CardLibrary, state: MatchState): number {
  return getModifiedAtk(m, library, state.reverseStatMods);
}

function attackersOf(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): CardInstance[] {
  return getMonsters(state.zones[seat])
    .filter(
      (m) =>
        m.faceUp &&
        m.position === CardPosition.ATTACK &&
        !m.attackedThisTurn &&
        !attackLockCode(state, seat, m),
    )
    .sort((a, b) => atkOf(b, library, state) - atkOf(a, library, state));
}

/**
 * A face-down spell/trap that has sat a turn might still be Mirror Force.
 * After any attack this Battle Phase, a Set that is still face-down was
 * declined, so the rest of the phase can swing. Easy ignores the risk.
 */
function freshBackrowScary(state: MatchState, seat: Seat): boolean {
  if (!state.seats[seat].isAI) return false;
  if (plannerDifficulty(state, seat) === 'easy') return false;
  if (getMonsters(state.zones[seat]).some((m) => m.attackedThisTurn)) return false;
  const opp = otherSeat(seat);
  return getSpellTraps(state.zones[opp]).some((c) => backrowStillScary(state, seat, c));
}

function barrierOf(
  m: CardInstance,
  library: CardLibrary,
  state: MatchState,
): Barrier {
  const isAtk = m.position === CardPosition.ATTACK;
  const stat = isAtk
    ? getModifiedAtk(m, library, state.reverseStatMods)
    : getModifiedDef(m, library, state.reverseStatMods);
  // Face-up Attack threats outrank Defense walls of the same number.
  const threat = isAtk ? stat + 500 : stat;
  return { monster: m, stat, isAtk, threat };
}

/** Destroy the target and survive. Equal ATK is a trade, not this. */
function destroysWithoutDying(atk: number, bar: Barrier): boolean {
  return atk > bar.stat;
}

/** Equality destroys in Attack Position; Defense needs a strictly higher ATK. */
function canDestroy(atk: number, bar: Barrier): boolean {
  if (bar.isAtk) return atk >= bar.stat;
  return atk > bar.stat;
}

function lpFromHit(atk: number, bar: Barrier): number {
  if (bar.isAtk) return Math.max(0, atk - bar.stat);
  return 0;
}

/**
 * Dark Elf pays 1000 to attack. Jirai Gumo can halve Life Points. Bistro
 * Butcher draws the opponent a card whenever the hit deals damage.
 */
function filterRiskyAttacks(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  plans: AttackPlan[],
): AttackPlan[] {
  const level = plannerDifficulty(state, seat);
  const mine = getMonsters(state.zones[seat]);
  return plans.filter((plan) => {
    const attacker = mine.find((monster) => monster.uid === plan.attackerUid);
    if (!attacker) return false;
    const id = Number(attacker.cardId);
    if (id === DARK_ELF) return darkElfAttackOk(state, library, seat, attacker, plan.targetUid);
    if (id === JIRAI_GUMO) return jiraiAttackOk(state, library, seat, attacker, plan.targetUid, level);
    if (id === THE_BISTRO_BUTCHER) {
      return bistroAttackOk(state, library, seat, attacker, plan.targetUid, level);
    }
    return true;
  });
}

function battleDamage(
  state: MatchState,
  library: CardLibrary,
  attacker: CardInstance,
  targetUid: string | null,
): { damage: number; destroys: boolean; equalTrade: boolean; unknown: boolean; body: number } {
  const atk = atkOf(attacker, library, state);
  if (!targetUid) return { damage: atk, destroys: false, equalTrade: false, unknown: false, body: 0 };
  let target: CardInstance | undefined;
  for (const side of [0, 1] as Seat[]) {
    target = getMonsters(state.zones[side]).find((monster) => monster.uid === targetUid);
    if (target) break;
  }
  if (!target || !target.faceUp) {
    return { damage: 0, destroys: false, equalTrade: false, unknown: true, body: 0 };
  }
  const attackPos = target.position === CardPosition.ATTACK;
  const body = attackPos
    ? getModifiedAtk(target, library, state.reverseStatMods)
    : getModifiedDef(target, library, state.reverseStatMods);
  const equalTrade = attackPos && atk === body;
  const destroys = atk > body;
  const damage = attackPos && destroys ? atk - body : 0;
  return { damage, destroys, equalTrade, unknown: false, body };
}

function darkElfAttackOk(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  attacker: CardInstance,
  targetUid: string | null,
): boolean {
  if (state.zones[seat].lifePoints < DARK_ELF_COST) return false;
  const hit = battleDamage(state, library, attacker, targetUid);
  if (hit.unknown) return plannerDifficulty(state, seat) === 'easy';
  if (hit.equalTrade) return false;
  if (!targetUid) return hit.damage > DARK_ELF_COST;
  if (!hit.destroys) return false;
  if (hit.damage > DARK_ELF_COST) return true;
  return hit.body >= 1500;
}

function jiraiAttackOk(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  attacker: CardInstance,
  targetUid: string | null,
  level: ReturnType<typeof plannerDifficulty>,
): boolean {
  if (level === 'easy' || level === 'test') return true;
  const hit = battleDamage(state, library, attacker, targetUid);
  if (hit.unknown || hit.equalTrade) return false;
  const oppLp = state.zones[otherSeat(seat)].lifePoints;
  if (hit.damage >= oppLp) return true;
  return hit.damage >= 1000;
}

function bistroAttackOk(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  attacker: CardInstance,
  targetUid: string | null,
  level: ReturnType<typeof plannerDifficulty>,
): boolean {
  const hit = battleDamage(state, library, attacker, targetUid);
  if (hit.damage <= 0) return true;
  const oppLp = state.zones[otherSeat(seat)].lifePoints;
  if (hit.damage >= oppLp) return true;
  const seen = new Set<string>();
  for (const card of state.zones[otherSeat(seat)].hand) {
    if (EXODIA_PIECE_SET.has(card.cardId)) seen.add(card.cardId);
  }
  if (seen.size >= 3) return false;
  if (aiIsHighDifficulty(level) && hit.damage < 1000) {
    return state.zones[otherSeat(seat)].hand.length > 4;
  }
  return true;
}

/**
 * Assign attackers to threats: unique killers first, then strongest attacker
 * onto the strongest remaining threat it can destroy without dying, then a
 * 1-for-1. Leftover attackers still run into Sets when a face-up stays that
 * they cannot destroy. The smallest legal body takes each Set.
 */
export function assignAttacks(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): AttackPlan[] {
  const opp = otherSeat(seat);
  const attackers = attackersOf(state, library, seat);
  const forced = magnetismTargets(state, opp);
  const oppMonsters = forced.length > 0 ? forced : getMonsters(state.zones[opp]);
  const faceUp = oppMonsters.filter((m) => m.faceUp);
  const faceDown = oppMonsters.filter((m) => !m.faceUp);
  if (attackers.length === 0) return [];
  if (oppMonsters.length === 0) {
    return attackers.map((a) => ({ attackerUid: a.uid, targetUid: null }));
  }

  const usedA = new Set<string>();
  const usedB = new Set<string>();
  const plans: AttackPlan[] = [];
  const threats = faceUp
    .map((m) => barrierOf(m, library, state))
    .sort((a, b) => b.threat - a.threat);
  const unknownSets = faceDown;

  const freeKillers = (bar: Barrier): CardInstance[] =>
    attackers.filter(
      (a) => !usedA.has(a.uid) && destroysWithoutDying(atkOf(a, library, state), bar),
    );
  const freeDestroyers = (bar: Barrier): CardInstance[] =>
    attackers.filter(
      (a) => !usedA.has(a.uid) && canDestroy(atkOf(a, library, state), bar),
    );

  for (const bar of threats) {
    const killers = freeDestroyers(bar);
    if (killers.length === 1) {
      plans.push({ attackerUid: killers[0].uid, targetUid: bar.monster.uid });
      usedA.add(killers[0].uid);
      usedB.add(bar.monster.uid);
    }
  }
  for (const bar of threats) {
    if (usedB.has(bar.monster.uid)) continue;
    const killers = freeKillers(bar);
    if (killers.length === 0) continue;
    plans.push({ attackerUid: killers[0].uid, targetUid: bar.monster.uid });
    usedA.add(killers[0].uid);
    usedB.add(bar.monster.uid);
  }

  for (const bar of threats) {
    if (usedB.has(bar.monster.uid)) continue;
    const traders = attackers.filter(
      (a) =>
        !usedA.has(a.uid) &&
        canDestroy(atkOf(a, library, state), bar) &&
        !destroysWithoutDying(atkOf(a, library, state), bar),
    );
    if (traders.length === 0) continue;
    plans.push({ attackerUid: traders[0].uid, targetUid: bar.monster.uid });
    usedA.add(traders[0].uid);
    usedB.add(bar.monster.uid);
  }

  // Leftover direct attackers still hit Life Points while another monster
  // fights. Ring of Magnetism makes a direct attack illegal.
  if (forced.length === 0) {
    for (const a of attackers) {
      if (usedA.has(a.uid) || !cardAttacksDirectly(a.cardId)) continue;
      plans.push({ attackerUid: a.uid, targetUid: null });
      usedA.add(a.uid);
    }
  }

  const leftover = attackers.filter((a) => !usedA.has(a.uid));
  if (unknownSets.length === 0) return filterRiskyAttacks(state, library, seat, plans);
  const probes = [...leftover].sort(
    (a, b) => atkOf(a, library, state) - atkOf(b, library, state),
  );
  let setIdx = 0;
  for (const a of probes) {
    const atk = atkOf(a, library, state);
    if (atk < SET_ATTACK_MIN_ATK) continue;
    if (setIdx >= unknownSets.length) break;
    plans.push({ attackerUid: a.uid, targetUid: unknownSets[setIdx].uid });
    setIdx += 1;
  }
  return filterRiskyAttacks(state, library, seat, plans);
}

/**
 * Attacks the live planner will actually declare. Normal+ pokes untested
 * backrow with a single attack; the forecast must not assume the rest land.
 */
function committedAttacks(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): AttackPlan[] {
  const plans = assignAttacks(state, library, seat);
  if (plans.length === 0 || !freshBackrowScary(state, seat)) return plans;
  return [cheapestPlan(state, library, seat, plans)];
}

function cheapestPlan(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  plans: AttackPlan[],
): AttackPlan {
  let best = plans[0];
  let bestAtk = Infinity;
  for (const plan of plans) {
    const attacker = getMonsters(state.zones[seat]).find((m) => m.uid === plan.attackerUid);
    const atk = attacker ? atkOf(attacker, library, state) : Infinity;
    if (atk < bestAtk) {
      best = plan;
      bestAtk = atk;
    }
  }
  return best;
}

function stallForExodia(state: MatchState, library: CardLibrary, seat: Seat): boolean {
  const ids = new Set(
    state.zones[seat].hand.filter((c) => EXODIA_PIECE_SET.has(c.cardId)).map((c) => c.cardId),
  );
  if (ids.size < 3) return false;
  const opp = otherSeat(seat);
  if (getMonsters(state.zones[opp]).length > 0) return true;
  let damage = 0;
  for (const m of getMonsters(state.zones[seat])) {
    if (m.faceUp && m.position === CardPosition.ATTACK && !m.attackedThisTurn) {
      damage += atkOf(m, library, state);
    }
  }
  return damage < state.zones[opp].lifePoints;
}

/**
 * Next Battle action from the matching, or ADVANCE_PHASE when nothing is safe.
 */
export function planNextAttack(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction {
  if (isOpeningTurn(state)) return ADVANCE;
  if (state.zones[seat].attackLockTurns > 0) return ADVANCE;
  if (stallForExodia(state, library, seat)) return ADVANCE;
  const opp = otherSeat(seat);
  if (state.battleDamageImmune[opp] && state.battleDestroyImmune[opp]) return ADVANCE;
  const next = committedAttacks(state, library, seat)[0];
  if (!next) return ADVANCE;
  if (next.targetUid === null) {
    return { type: ActionType.DIRECT_ATTACK, attackerUid: next.attackerUid };
  }
  return {
    type: ActionType.DECLARE_ATTACK,
    attackerUid: next.attackerUid,
    targetUid: next.targetUid,
  };
}

/**
 * Forecast of `attackerSeat` swinging. Face-down defenders stay unknown, so
 * an attack into a Set does not count as damage or removal.
 */
export function forecastCombat(
  state: MatchState,
  library: CardLibrary,
  attackerSeat: Seat,
): CombatForecast {
  const defender = otherSeat(attackerSeat);
  const plans = committedAttacks(state, library, attackerSeat);
  const oppMonsters = getMonsters(state.zones[defender]);
  let attackerDamage = 0;
  let defenderAtkDestroyed = 0;
  let attackerAtkLost = 0;
  const killed = new Set<string>();
  const usedA = new Set<string>();

  for (const plan of plans) {
    const attacker = getMonsters(state.zones[attackerSeat]).find(
      (m) => m.uid === plan.attackerUid,
    );
    if (!attacker) continue;
    usedA.add(attacker.uid);
    const aAtk = atkOf(attacker, library, state);
    if (plan.targetUid === null) {
      attackerDamage += aAtk;
      continue;
    }
    const target = oppMonsters.find((m) => m.uid === plan.targetUid);
    if (!target) continue;
    if (!target.faceUp) continue;
    const bar = barrierOf(target, library, state);
    if (!canDestroy(aAtk, bar)) continue;
    killed.add(target.uid);
    if (destroysWithoutDying(aAtk, bar)) {
      attackerDamage += lpFromHit(aAtk, bar);
      if (bar.isAtk) defenderAtkDestroyed += bar.stat;
    } else if (bar.isAtk) {
      attackerAtkLost += aAtk;
      defenderAtkDestroyed += bar.stat;
    }
  }

  const unclearedDefenderAtk = oppMonsters
    .filter((m) => m.faceUp && m.position === CardPosition.ATTACK && !killed.has(m.uid))
    .reduce((sum, m) => sum + atkOf(m, library, state), 0);

  const leftoverAttackers = attackersOf(state, library, attackerSeat)
    .filter((a) => !usedA.has(a.uid)).length;

  const remaining = oppMonsters.filter((m) => !killed.has(m.uid));
  const unknownLeft = remaining.some((m) => !m.faceUp);
  const publicLeft = remaining.filter((m) => m.faceUp);
  const lethal =
    !unknownLeft &&
    unclearedDefenderAtk === 0 &&
    publicLeft.length === 0 &&
    attackerDamage >= state.zones[defender].lifePoints &&
    attackerDamage > 0;

  return {
    attackerDamage,
    defenderAtkDestroyed,
    attackerAtkLost,
    lethal,
    unclearedDefenderAtk,
    leftoverAttackers,
  };
}

/**
 * ATK still in hand from an Equip or Field that we could play this Main Phase.
 * Already-attached equips and the face-up Field Spell are in getModifiedAtk.
 */
export function pendingHandAtkBoost(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  monster: CardInstance,
): number {
  const race = getCardData(library, monster.cardId).race;
  let boost = 0;
  let usedEquip = false;
  for (const c of state.zones[seat].hand) {
    if (c.uid === monster.uid) continue;
    if (!usedEquip) {
      const eq = getEquipConfig(Number(c.cardId));
      if (eq && (!eq.raceFilter || eq.raceFilter === race)) {
        boost += eq.atkBoost;
        usedEquip = true;
      }
    }
    const field = getFieldSpellConfig(Number(c.cardId));
    if (!field || !race) continue;
    if (field.boostedRaces.includes(race)) boost += field.atkBoost;
    if (field.penalizedRaces?.includes(race)) boost -= field.atkBoost;
  }
  return state.reverseStatMods ? -boost : boost;
}

/**
 * Whether this monster should sit in Attack Position: lethal, an empty field,
 * a face-up it can destroy, a 1-for-1, or a Set its ATK is willing to enter.
 * A high-DEF wall stays down unless the crash is worth the printed DEF.
 */
export function shouldMonsterBeInAttack(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  monster: CardInstance,
  includeHandBoost = true,
): boolean {
  const opp = otherSeat(seat);
  const oppMonsters = getMonsters(state.zones[opp]);
  const ownAtk = atkOf(monster, library, state);
  // A 0 ATK body only gives itself away. Wall of Illusion is printed 1000/1850
  // in the catalog; its job is still the 1850 wall, so it stays in defense
  // unless the direct attack is lethal.
  if (ownAtk <= 0) return false;
  if (monster.cardId === '13945283') {
    return oppMonsters.length === 0 && ownAtk >= state.zones[opp].lifePoints;
  }
  // Jirai Gumo and Dark Elf stay in Attack. The attack filter refuses the
  // coin or the 1000 Life Point cost unless the hit pays for it.
  const level = plannerDifficulty(state, seat);
  const sitId = Number(monster.cardId);
  if (
    (sitId === JIRAI_GUMO || sitId === DARK_ELF) &&
    level !== 'easy' &&
    level !== 'test'
  ) {
    return true;
  }
  const printedDef = getModifiedDef(monster, library, state.reverseStatMods);
  const wall = printedDef >= ownAtk + 800 && printedDef >= 1500;
  const atk =
    ownAtk +
    (includeHandBoost ? pendingHandAtkBoost(state, library, seat, monster) : 0);
  if (oppMonsters.length === 0) {
    if (wall) return ownAtk >= state.zones[opp].lifePoints;
    return true;
  }

  const ourForecast = forecastCombat(state, library, seat);
  if (ourForecast.lethal) return true;

  const known = oppMonsters.filter((m) => m.faceUp);
  if (wall) {
    return known.some(
      (m) =>
        destroysWithoutDying(atk, barrierOf(m, library, state)) &&
        atkOf(m, library, state) >= printedDef,
    );
  }
  if (known.some((m) => destroysWithoutDying(atk, barrierOf(m, library, state)))) {
    return true;
  }
  const ties = known.some(
    (m) => m.position === CardPosition.ATTACK && atkOf(m, library, state) === atk,
  );
  if (ties) return true;
  const hasSet = oppMonsters.some((m) => !m.faceUp);
  if (hasSet && atk >= SET_ATTACK_MIN_ATK) return true;

  const bestOppAtk = known
    .filter((m) => m.position === CardPosition.ATTACK)
    .reduce((max, m) => Math.max(max, atkOf(m, library, state)), 0);
  if (bestOppAtk > atk) return false;
  return atk >= getModifiedDef(monster, library, state.reverseStatMods);
}

/**
 * Combined combat outlook for `seat` (our swing minus theirs).
 * Used to decide tributes and board wipes without re-weighting raw ATK sums.
 */
export function combatOutlookScore(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  const our = forecastCombat(state, library, seat);
  const their = forecastCombat(state, library, otherSeat(seat));
  let score = our.attackerDamage * 0.4 + our.defenderAtkDestroyed * 0.5;
  score -= their.attackerDamage + their.defenderAtkDestroyed * 0.5;
  score -= our.attackerAtkLost * 0.45;
  if (our.lethal) score += 50_000;
  if (their.lethal) score -= 40_000;
  return score;
}

export const BURN_DAMAGE: Record<string, number> = {
  '76103675': 200, // Sparks
  '46130346': 500, // Hinotama
  '73134081': 600, // Final Flame
  '19523799': 800, // Ookazi
  '46918794': 1000, // Tremendous Fire
};
